import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { publicApi } from '../../api/client';
import { PRACTICE_TYPE_LABELS, type PublicSample } from '../../api/types';
import i18n, { currentLocale } from '../../i18n';
import { PUBLIC_CONTACT, SITE } from '../../siteConfig';
import { TRADITION_LABELS } from '../../theme/tokens';

/** One titled block of plain-language copy, stored as an array in the locale files. */
export interface ProseSection {
  heading: string;
  paragraphs?: string[];
  items?: string[];
}

/** An array-valued translation (sections, lists, FAQs), or [] if the key is missing. */
export function useTranslatedList<T>(key: string): T[] {
  const { t } = useTranslation();
  const value = t(key, { returnObjects: true }) as unknown;
  return Array.isArray(value) ? (value as T[]) : [];
}

export function ProseSections({ sections, idPrefix }: { sections: ProseSection[]; idPrefix: string }) {
  return (
    <>
      {sections.map((section, i) => (
        <section key={i} className="prose-section" aria-labelledby={`${idPrefix}-${i}`}>
          <h2 id={`${idPrefix}-${i}`}>{section.heading}</h2>
          {section.paragraphs?.map((paragraph, j) => <p key={j}>{paragraph}</p>)}
          {section.items && section.items.length > 0 && (
            <ul>
              {section.items.map((item, j) => <li key={j}>{item}</li>)}
            </ul>
          )}
        </section>
      ))}
    </>
  );
}

export function PageIntro({ title, lead }: { title: string; lead?: string }) {
  return (
    <header className="public-page-intro">
      <h1 className="page-title">{title}</h1>
      {lead && <p className="public-lead">{lead}</p>}
    </header>
  );
}

/** Privacy and Terms are plain-language drafts pending Mauro's (and legal) review -- said on-page. */
export function DraftBanner() {
  const { t } = useTranslation();
  return (
    <div className="draft-banner" role="note">
      <strong>{t('public.draftBanner.title')}</strong>
      <p>{t('public.draftBanner.body', { date: SITE.legalDraftDate })}</p>
    </div>
  );
}

export function hasPublicContact(): boolean {
  return Boolean(PUBLIC_CONTACT.supportEmail || PUBLIC_CONTACT.supportUrl);
}

/** Renders PUBLIC_CONTACT gracefully: nothing is invented while its fields are empty. */
export function ContactDetails({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  if (!hasPublicContact()) {
    return <p className={compact ? 'public-footer-note' : 'contact-pending'}>{t('public.contact.pending')}</p>;
  }
  return (
    <ul className={compact ? 'contact-list compact' : 'contact-list'}>
      {PUBLIC_CONTACT.supportEmail && (
        <li>
          {t('public.contact.email')}{' '}
          <a href={`mailto:${PUBLIC_CONTACT.supportEmail}`}>{PUBLIC_CONTACT.supportEmail}</a>
        </li>
      )}
      {PUBLIC_CONTACT.supportUrl && (
        <li>
          <a href={PUBLIC_CONTACT.supportUrl} target="_blank" rel="noopener noreferrer">
            {t('public.contact.url')}
            <span className="visually-hidden"> {t('a11y.opensInNewTab')}</span>
          </a>
        </li>
      )}
    </ul>
  );
}

export function OperatorDetails() {
  const { t } = useTranslation();
  if (!PUBLIC_CONTACT.legalEntityName) return <p>{t('public.contact.operatorPending')}</p>;
  return (
    <p>
      {t('public.contact.operator', { name: PUBLIC_CONTACT.legalEntityName })}
      {PUBLIC_CONTACT.postalAddress ? ` — ${PUBLIC_CONTACT.postalAddress}` : ''}
    </p>
  );
}

/** Never relabels content: "reviewed" only when the server says so AND names the reviewer. */
export function reviewStatusLabel(sample: Pick<PublicSample, 'reviewerStatus' | 'reviewerName'>): string {
  return sample.reviewerStatus === 'reviewed' && sample.reviewerName
    ? i18n.t('reviewStatus.reviewed', { name: sample.reviewerName })
    : i18n.t('reviewStatus.draft');
}

type SampleState =
  | { status: 'loading'; sample: null }
  | { status: 'ready'; sample: PublicSample }
  | { status: 'error'; sample: null };

/** GET /api/public/sample in the current UI language; refetches when the language changes. */
export function usePublicSample() {
  const { i18n: instance } = useTranslation();
  const [state, setState] = useState<SampleState>({ status: 'loading', sample: null });
  const [attempt, setAttempt] = useState(0);
  const lang = currentLocale();

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', sample: null });
    publicApi
      .getPublicSample(lang)
      .then((res) => {
        if (!cancelled) setState({ status: 'ready', sample: res.sample });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', sample: null });
      });
    return () => {
      cancelled = true;
    };
  }, [lang, attempt, instance.language]);

  return { ...state, retry: () => setAttempt((a) => a + 1) };
}

/** Tradition, type, duration, the ACTUAL content language (with an explicit fallback note) and
 * review status -- what the review asked every sample card to show. */
export function SampleMeta({ sample }: { sample: PublicSample }) {
  const { t } = useTranslation();
  const minutes = Math.max(1, Math.round(sample.durationSeconds / 60));
  return (
    <dl className="sample-meta">
      <dt>{t('public.sample.meta.tradition')}</dt>
      <dd>{TRADITION_LABELS[sample.tradition] ?? sample.tradition}</dd>
      <dt>{t('public.sample.meta.type')}</dt>
      <dd>{PRACTICE_TYPE_LABELS[sample.type] ?? sample.type}</dd>
      <dt>{t('public.sample.meta.duration')}</dt>
      <dd>{t('public.sample.minutes', { count: minutes })}</dd>
      <dt>{t('public.sample.meta.language')}</dt>
      <dd>
        {t(`languages.${sample.contentLanguage}`)}
        {sample.languageFallback && (
          <span className="sample-meta-note">
            {' '}— {t('public.sample.fallback', { requested: t(`languages.${sample.requestedLanguage}`) })}
          </span>
        )}
      </dd>
      <dt>{t('public.sample.meta.status')}</dt>
      <dd><span className="status-badge">{reviewStatusLabel(sample)}</span></dd>
    </dl>
  );
}
